import React, { useEffect, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Table,
  Button,
  Row,
  Col,
  Form,
  DatePicker,
  Select,
  Collapse,
  Input,
  Tooltip,
  Modal,
  message,
} from "antd";
import moment from "moment";
import { checkIsLoaded, checkIsLoading } from "../../../lib/utils/checkLoadingStatus";
import { ReactComponent as DeleteIcon } from "../../../img/delete-round.svg";
import { ReactComponent as DotsIcon } from "../../../img/3dots.svg";
import { hasAccess } from "../../RolesModule/hasAccess";
import {
  MENU_ACCOUNT_PAYMENTS_WRITE,
  MENU_PAYMENTLOAD_WRITE,
} from "../../../lib/const/permissions";
import SpanNumberFormat from "../../common/SpanNumberFormat";
import { displayFormat } from "../../../lib/utils/formatDate";
import HistoryAndPropertyModal from "../../HistoryAndPropertyModal/HistoryAndPropertyModal";
import ToggleColumns from "../../ToggleableVersionsTable/ToggleColumns";
import { deleteBatchRoute } from "../../../lib/routes/payments-loader";
import MultipleSelect from "../../common/MultipleSelect";
import "./StatementsTable.less";

const StatementsTable = ({
  initialSearchValues,
  defaultSearchValues,
  handleSearchStatements,
  setStatementsSearchParams,
}) => {
  const history = useHistory();
  const { url } = useRouteMatch();
  const { statements, statementsLoading, totalElements, currentPage } = useSelector(
    (state) => state.statements
  );
  const { batchStatus } = useSelector((state) => state.dicts);
  // console.log(statements);
  const [tableDate, setTableDate] = useState([]);

  const [statementsSearchForm] = Form.useForm();
  const [activeCollapseKey, setActiveCollapseKey] = useState("1");
  const [historyObj, setHistoryObj] = useState();
  const [deletedBatchId, setDeletedBatchId] = useState(0);

  const handleDeleteBatch = () => {
    axios
      .delete(deleteBatchRoute(deletedBatchId))
      .then(() => {
        message.success("Выписка отменена");
        handleSearchStatements({ page: currentPage - 1 });
      })
      .catch(() => {
        message.error("Ошибка отмены выписки");
      })
      .finally(() => {
        setDeletedBatchId(0);
      });
  };

  const displayHistoryModal = (objId) => {
    setHistoryObj(objId);
  };

  useEffect(() => {
    if (checkIsLoaded(statementsLoading)) {
      setTableDate(
        statements.map((statement) => ({
          key: statement.batch.id,
          id: statement.batch.id,
          fileName: statement.batch.fileName,
          batchDate: statement.batch.batchDate,
          uploadedDate: statement.batchInfo.uploadedDate,
          processedDate: statement.batchInfo.processedDate,
          linesSumAmount: statement.batchInfo.linesSumAmount,
          currency: statement.batch.currency.name,
          batchStatusName: statement.batch.batchStatus.name,
          batchStatusId: statement.batch.batchStatus.id,
        }))
      );
    }
  }, [statementsLoading]);

  const columnWidths = {
    id: "9%",
    fileName: "22%",
    batchDate: "9%",
    uploadedDate: "13%",
    processedDate: "13%",
    linesSumAmount: "9%",
    currency: "8%",
    batchStatusName: "10%",
    actions: "3%",
    buttons: "3%",
  };

  const [columns, setColumns] = useState([
    {
      title: "ID выписки",
      dataIndex: "id",
      key: "id",
      width: columnWidths.id,
    },
    {
      title: "Файл",
      dataIndex: "fileName",
      key: "fileName",
      width: columnWidths.fileName,
    },
    {
      title: "Дата выписки",
      dataIndex: "batchDate",
      key: "batchDate",
      width: columnWidths.batchDate,
      render: (date) => (date ? moment(date).format("DD.MM.YYYY") : ""),
    },
    {
      title: "Дата загрузки",
      dataIndex: "uploadedDate",
      key: "uploadedDate",
      width: columnWidths.uploadedDate,
      render: (date) => (date ? moment(date).format("DD.MM.YYYY HH:mm:ss") : ""),
    },
    {
      title: "Дата обработки",
      dataIndex: "processedDate",
      key: "processedDate",
      width: columnWidths.processedDate,
      render: (date) => (date ? moment(date).format("DD.MM.YYYY HH:mm:ss") : ""),
    },
    {
      title: "Сумма",
      dataIndex: "linesSumAmount",
      key: "linesSumAmount",
      width: columnWidths.linesSumAmount,
      render: (linesSumAmount) => SpanNumberFormat({ amount: linesSumAmount }),
    },
    {
      title: "Валюта",
      dataIndex: "currency",
      key: "currency",
      width: columnWidths.currency,
    },
    {
      title: "Статус",
      dataIndex: "batchStatusName",
      key: "batchStatusName",
      width: columnWidths.batchStatusName,
    },
    {
      key: "actions",
      render: (v, record) => (
        <Tooltip title="Отменить выписку">
          {[1, 2, 5, 10].includes(record.batchStatusId) && (
            <Button
              icon={<DeleteIcon />}
              type="text"
              className="button-icon account-payments__btn"
              style={{ outline: "none", paddingTop: "4px" }}
              onClick={(e) => {
                e.stopPropagation();
                setDeletedBatchId(record.id);
              }}
            />
          )}
        </Tooltip>
      ),
      width: columnWidths.actions,
    },
    {
      key: "buttons",
      render: (v, record) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            displayHistoryModal(record);
          }}
          className="button-icon account-payments__btn"
          style={{ outline: "none", paddingTop: "4px" }}
          icon={<DotsIcon />}
        />
      ),
      width: columnWidths.buttons,
    },
  ]);

  const handleReset = () => {
    statementsSearchForm.setFieldsValue(defaultSearchValues);
    setStatementsSearchParams(defaultSearchValues);
    handleSearchStatements({
      page: 0,
      initialSearch: true,
    });
  };

  const renderStatementsSearch = () => {
    const onFinish = () => {
      handleSearchStatements({
        page: 0,
      });
    };
    return (
      <div className="statement-table-search">
        <Collapse
          activeKey={activeCollapseKey}
          expandIconPosition="right"
          className="filter-collapse"
          onChange={() => {
            setActiveCollapseKey((prev) => (prev ? "" : "1"));
          }}
        >
          <Collapse.Panel
            key="1"
            header={
              !activeCollapseKey && <div className="bulk__panel_header">Показать фильтры</div>
            }
          >
            <Form
              form={statementsSearchForm}
              initialValues={initialSearchValues}
              onFinish={onFinish}
              onValuesChange={(field) => {
                setStatementsSearchParams((prev) => ({ ...prev, ...field }));
              }}
            >
              <Row>
                <Col span={7}>
                  <Form.Item
                    name="period"
                    labelCol={{ span: 8 }}
                    label="Дата выписки"
                    labelAlign="left"
                  >
                    <DatePicker.RangePicker
                      style={{ width: "100%" }}
                      allowClear
                      format={displayFormat}
                    />
                  </Form.Item>
                </Col>
                <Col span={15} offset={1}>
                  <Form.Item
                    name="includeBatchStatusIds"
                    label="Статус"
                    style={{ justifyContent: "flex-start" }}
                  >
                    <MultipleSelect
                      allValues={batchStatus.map(({ id }) => id)}
                      style={{ width: "100%" }}
                      mode="multiple"
                      allowClear
                      placeholder="Выбрать"
                      maxTagCount={6}
                    >
                      {batchStatus.map(({ id, name }) => (
                        <Select.Option key={id} value={id}>
                          {name}
                        </Select.Option>
                      ))}
                    </MultipleSelect>
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="space-between">
                <Col span={7}>
                  <Form.Item
                    name="batchIds"
                    label="ID выписок"
                    labelAlign="left"
                    labelCol={{ span: 8 }}
                    style={{ marginBottom: "10px" }}
                    rules={[
                      {
                        validator: (x, value) => {
                          if (value && /[^0-9,]/.test(value)) {
                            return Promise.reject();
                          }
                          return Promise.resolve();
                        },
                        message: "В качестве разделителя может использоваться только запятая",
                      },
                    ]}
                  >
                    <Input placeholder='Введите ID выписок через ","' />
                  </Form.Item>
                </Col>
                <Col span={7} style={{ textAlign: "right" }}>
                  <Button onClick={handleReset}>Сброс параметров</Button>
                  <Button htmlType="submit" type="primary">
                    Применить
                  </Button>
                </Col>
              </Row>
            </Form>
          </Collapse.Panel>
        </Collapse>
      </div>
    );
  };

  return (
    <div className="statement-table">
      <Row justify="space-between" align="middle" style={{ padding: "8px 12px 12px" }}>
        <Col>
          <h1 style={{ marginBottom: 0 }}>Банковские выписки</h1>
        </Col>
        <Col>
          {hasAccess(MENU_PAYMENTLOAD_WRITE) && (
            <Button
              className="ant-btn ant-btn-primary"
              type="primary"
              onClick={() => {
                history.push(`${url}/upload`);
                handleReset();
              }}
            >
              Загрузить выписку
            </Button>
          )}
        </Col>
      </Row>
      {renderStatementsSearch()}
      <div style={{ marginLeft: "97%", marginTop: "7px" }}>
        <ToggleColumns tableName="statements" columns={columns} setColumns={setColumns} />
      </div>
      <Table
        columns={columns.filter(({ hidden }) => !hidden)}
        dataSource={tableDate}
        size="small"
        loading={checkIsLoading(statementsLoading)}
        pagination={{
          position: ["bottomCenter"],
          hideOnSinglePage: true,
          onChange: (pageNum) => handleSearchStatements({ page: pageNum - 1 }),
          showSizeChanger: false,
          current: currentPage,
          total: totalElements,
        }}
        onRow={(record) => ({
          onClick: () => {
            history.push(`${url}/${record.id}`);
          },
        })}
      />
      <Modal
        okText="Далее"
        cancelText="Отмена"
        visible={Boolean(deletedBatchId)}
        title="Отмена выписки"
        onCancel={() => {
          setDeletedBatchId(0);
        }}
        onOk={handleDeleteBatch}
      >
        Вы действительно хотите отменить выписку? <br />
        Дальнейшие действия с выпиской будут не доступны.
      </Modal>
      <HistoryAndPropertyModal
        objectTypeSysname="payment_load_batch"
        objectId={historyObj?.id}
        handleHideModal={() => setHistoryObj()}
        visibleModal={historyObj?.id}
        dateObjectFromForCalendar={historyObj?.chargePeriodFrom}
        dateObjectToForCalendar={historyObj?.chargePeriodTo}
        hasAccessToEdit={hasAccess(MENU_ACCOUNT_PAYMENTS_WRITE)}
      />
    </div>
  );
};

export default StatementsTable;
